import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { projectsAPI } from '../../helpers/api';

interface Personnel {
  id: string;
  firstName: string;
  lastName: string;
  title: string | null;
  phone: string | null;
}

interface SitePersonnelEntry {
  id: string;
  assignedAt: string;
  personnel: Personnel;
}

interface ProjectPersonnel {
  id: string;
  personnel: Personnel;
}

interface Site {
  id: string;
  name: string;
  code: string;
  project: { name: string; city: { name: string } };
}

interface SitePersonnelModalProps {
  isOpen: boolean;
  toggle: () => void;
  site: Site | null;
  onUpdate?: () => void;
}

const SitePersonnelModal: React.FC<SitePersonnelModalProps> = ({
  isOpen,
  toggle,
  site,
  onUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [personnel, setPersonnel] = useState<SitePersonnelEntry[]>([]);
  const [unassignedPersonnel, setUnassignedPersonnel] = useState<ProjectPersonnel[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [adding, setAdding] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && site) {
      fetchData();
    }
  }, [isOpen, site]);

  const fetchData = async () => {
    if (!site) return;

    try {
      setLoading(true);
      const [personnelRes, unassignedRes] = await Promise.all([
        projectsAPI.getSitePersonnel(site.id),
        projectsAPI.getUnassignedPersonnel(site.id),
      ]);
      setPersonnel(personnelRes.data);
      setUnassignedPersonnel(unassignedRes.data);
    } catch (error) {
      console.error('Data fetch error:', error);
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (personnelId: string) => {
    if (!site) return;

    try {
      setAdding(personnelId);
      await projectsAPI.assignPersonnelToSite(site.id, personnelId);
      toast.success('Personel şantiyeye atandı');
      fetchData();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Atama başarısız');
    } finally {
      setAdding(null);
    }
  };

  const handleRemove = async (personnelId: string) => {
    if (!site) return;
    if (!window.confirm('Personeli şantiyeden çıkarmak istediğinize emin misiniz?')) return;

    try {
      setRemoving(personnelId);
      await projectsAPI.removePersonnelFromSite(site.id, personnelId);
      toast.success('Personel şantiyeden çıkarıldı');
      fetchData();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setRemoving(null);
    }
  };

  const filteredUnassigned = unassignedPersonnel.filter(
    (pp) =>
      `${pp.personnel.firstName} ${pp.personnel.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pp.personnel.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (!site) return null;

  const headerActions = (
    <>
      <button
        type="button"
        className="side-modal-expand-btn me-1"
        onClick={() => setShowAddPanel(!showAddPanel)}
        title={showAddPanel ? 'Listeye Dön' : 'Personel Ekle'}
        style={{ color: showAddPanel ? '#f06548' : '#0ab39c' }}
      >
        <i className={showAddPanel ? 'ri-arrow-left-line' : 'ri-user-add-line'}></i>
      </button>
      <button
        type="button"
        className="side-modal-expand-btn"
        onClick={toggleExpand}
        title={isExpanded ? 'Küçült' : 'Genişlet'}
      >
        <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
      </button>
    </>
  );

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Şantiye Personeli"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={headerActions}
      hideBackdrop
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {/* Header Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }} className="rounded-3 p-3 mb-3">
          <h4 className="mb-1" style={{ fontWeight: 600 }}>
            {site.name}
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            {site.project.city.name} • {site.project.name}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        ) : showAddPanel ? (
          /* Add Personnel Panel */
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }} className="rounded-3 p-3 mb-3">
            <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Personel Ekle
            </div>
            <Input
              type="text"
              placeholder="Personel ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mb-3"
            />
            {filteredUnassigned.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted mb-0">
                  {searchTerm
                    ? 'Aramayla eşleşen personel bulunamadı'
                    : 'Projede atanabilecek personel bulunmuyor'}
                </p>
                <small className="text-muted d-block mt-2">
                  <i className="ri-information-line me-1"></i>
                  Personellerin önce projeye atanması gerekir
                </small>
              </div>
            ) : (
              <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
                {filteredUnassigned.map((pp, idx) => (
                  <div
                    key={pp.id}
                    className="d-flex align-items-center justify-content-between px-3 py-2"
                    style={{ borderBottom: idx < filteredUnassigned.length - 1 ? '1px solid #e9ecef' : 'none' }}
                  >
                    <div>
                      <span style={{ fontWeight: 500 }}>{pp.personnel.firstName} {pp.personnel.lastName}</span>
                      {pp.personnel.title && (
                        <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>
                          ({pp.personnel.title})
                        </span>
                      )}
                    </div>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => handleAssign(pp.personnel.id)}
                      disabled={adding === pp.personnel.id}
                    >
                      {adding === pp.personnel.id ? <Spinner size="sm" /> : <i className="ri-add-line"></i>}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Personnel List */}
            <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }} className="rounded-3 p-3 mb-3">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Atanmış Personeller
                </span>
                <span className="text-muted small">{personnel.length} kişi</span>
              </div>
              {personnel.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted mb-3">Bu şantiyeye henüz personel atanmamış</p>
                  <Button color="primary" size="sm" onClick={() => setShowAddPanel(true)}>
                    <i className="ri-user-add-line me-1"></i>
                    Personel Ekle
                  </Button>
                </div>
              ) : (
                <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
                  {personnel.map((entry, idx) => (
                    <div
                      key={entry.id}
                      className="d-flex align-items-center justify-content-between px-3 py-2"
                      style={{ borderBottom: idx < personnel.length - 1 ? '1px solid #e9ecef' : 'none' }}
                    >
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {entry.personnel.firstName} {entry.personnel.lastName}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                          {entry.personnel.title || 'Görev belirtilmedi'}
                          {entry.personnel.phone && ` • ${entry.personnel.phone}`}
                        </div>
                      </div>
                      <Button
                        color="danger"
                        size="sm"
                        outline
                        onClick={() => handleRemove(entry.personnel.id)}
                        disabled={removing === entry.personnel.id}
                      >
                        {removing === entry.personnel.id ? (
                          <Spinner size="sm" />
                        ) : (
                          <i className="ri-close-line"></i>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info Note */}
            <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #299cdb' }} className="rounded-3 p-3 mb-3">
              <div className="d-flex align-items-start">
                <i className="ri-information-line text-info me-2" style={{ fontSize: '1.1rem' }}></i>
                <small className="text-muted">
                  Personeller önce projeye atanır, sonra projedeki personeller şantiyelere dağıtılabilir.
                </small>
              </div>
            </div>
          </>
        )}
      </div>
    </SideModal>
  );
};

export default SitePersonnelModal;
